import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", confirm: "",
    terms_accepted: false, privacy_accepted: false,
  });
  const [busy, setBusy] = useState(false);
  // In-flight guard — prevents a duplicate registration POST from
  // being fired by rapid double-submit (Enter key twice, refresh
  // during "Creating…", etc.) even before the React re-render that
  // sets `disabled={busy}` on the button. Ref is synchronous, unlike
  // state.
  const inFlight = useRef(false);
  const { register } = useAuth();
  const nav = useNavigate();
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (inFlight.current) return;
    if (form.password.length < 12) return toast.error("Password must be at least 12 characters.");
    if (form.password !== form.confirm) return toast.error("Passwords do not match.");
    if (!form.terms_accepted || !form.privacy_accepted)
      return toast.error("Please accept the Terms of Service and Privacy Policy.");
    inFlight.current = true;
    setBusy(true);
    try {
      const r = await register({
        first_name: form.first_name, last_name: form.last_name,
        email: form.email, password: form.password,
        terms_accepted: form.terms_accepted, privacy_accepted: form.privacy_accepted,
      });
      if (r.ok) { toast.success("Account created. Welcome to DocCircle."); nav("/onboarding"); }
      else toast.error(r.error);
    } finally {
      setBusy(false);
      inFlight.current = false;
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="register-page">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1741699428220-65f37f3fbbcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxkb2N0b3JhbCUyMHN0dWRlbnQlMjBzdHVkeWluZ3xlbnwwfHx8fDE3ODYyOTMwMzJ8MA&ixlib=rb-4.1.0&q=85" alt="Library" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-secondary/40" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12">
          <BookOpen className="h-6 w-6 text-primary mb-4" />
          <h2 className="font-serif text-4xl font-black tracking-tight max-w-md">You are exactly where you're supposed to be.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12 lg:px-16">
        <form onSubmit={submit} className="w-full max-w-lg space-y-5" data-testid="register-form">
          <div>
            <h1 className="font-serif text-3xl font-black tracking-tight">Create your account</h1>
            <p className="text-sm text-muted-foreground mt-2">Already have an account? <Link to="/login" className="text-primary underline underline-offset-4">Sign in</Link></p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>First name</Label><Input required data-testid="reg-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} /></div>
            <div><Label>Last name</Label><Input required data-testid="reg-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} /></div>
            <div className="col-span-2"><Label>Email</Label><Input type="email" required data-testid="reg-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div className="col-span-2"><Label>Password</Label><Input type="password" required minLength={12} data-testid="reg-password" value={form.password} onChange={(e) => set("password", e.target.value)} /><p className="text-[10px] text-muted-foreground mt-1">Minimum 12 characters. Mix upper, lower, digit, symbol (or use a 20+ char passphrase).</p></div>
            <div className="col-span-2"><Label>Confirm password</Label><Input type="password" required minLength={12} data-testid="reg-confirm" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} /></div>
          </div>
          <label className="flex items-start gap-3 text-sm cursor-pointer">
            <Checkbox checked={form.terms_accepted} onCheckedChange={(v) => set("terms_accepted", !!v)} data-testid="reg-terms" className="mt-0.5" />
            <span>I accept the <Link to="/terms" className="text-primary underline underline-offset-4">Terms of Service</Link>.</span>
          </label>
          <label className="flex items-start gap-3 text-sm cursor-pointer">
            <Checkbox checked={form.privacy_accepted} onCheckedChange={(v) => set("privacy_accepted", !!v)} data-testid="reg-privacy" className="mt-0.5" />
            <span>I acknowledge the <Link to="/privacy-policy" className="text-primary underline underline-offset-4">Privacy Policy</Link>.</span>
          </label>
          <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="reg-submit-btn">
            {busy ? "Creating…" : "Create account"}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            <Link to="/forgot-password" className="hover:text-foreground">Forgot password?</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
